import React from 'react';
import { X } from 'lucide-react';

interface DrawerProps {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onClose: () => void;
  closeLabel?: string;
  size?: 'md' | 'lg';
  className?: string;
}

const joinClasses = (...classes: Array<string | undefined | false>) =>
  classes.filter(Boolean).join(' ');

const FormDrawer: React.FC<DrawerProps> = ({
  open,
  title,
  description,
  children,
  footer,
  onClose,
  closeLabel = 'Close form drawer',
  size = 'md',
  className,
}) => {
  React.useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className="drawer-backdrop" role="presentation">
      <button className="drawer-scrim" type="button" aria-label={closeLabel} onClick={onClose} />
      <aside
        className={joinClasses('drawer-panel', 'form-drawer', `drawer-panel--${size}`, className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="form-drawer-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="form-drawer-title">{title}</h2>
            {description ? <p>{description}</p> : null}
          </div>
          <button className="icon-button drawer-close" type="button" aria-label={closeLabel} onClick={onClose}>
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="drawer-body">
          {children}
        </div>
        {footer ? <div className="drawer-footer">{footer}</div> : null}
      </aside>
    </div>
  );
};

export default FormDrawer;
